import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'tss-react/mui';
import { useSelector, useDispatch } from 'react-redux';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VimeoVideo from 'src/components/VimeoVideo';
import { STORAGE } from 'src/constants';
import { dismissWhatsNew } from 'src/actions/accountActions';
import whatsNewModalStyle from './whatsNewModal.style';

/* formatDate - Formats a date string as "Released MM/DD/YY" */
function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `Released ${mm}/${dd}/${yy}`;
}

/* buildImageUrl - Constructs full image URL from filename + container */
function buildImageUrl(filename, attachmentContainer, azAccountName) {
  if (!filename) return '';
  // If it starts with http, it's a direct URL
  if (filename.startsWith('http')) return filename;
  // Build Azure Blob URL
  if (attachmentContainer && azAccountName) {
    // CP's blob containers are private — the SAS is appended at render time
    // (same as every other CP image) so stored rows survive a SAS rotation.
    return `https://${azAccountName}.blob.core.windows.net/${attachmentContainer}/${filename}${STORAGE.sas}`;
  }
  return filename;
}

/* parseImageUrls - Parses contentValue for image blocks, returns array of URLs */
function parseImageUrls(contentValue, attachmentContainer, azAccountName) {
  if (!contentValue) return [];
  try {
    const parsed = JSON.parse(contentValue);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => buildImageUrl(item, attachmentContainer, azAccountName));
    }
  } catch (e) {
    // Not valid JSON — treat as a single URL/filename
  }
  return [buildImageUrl(contentValue, attachmentContainer, azAccountName)];
}

function WhatsNewModal({
  classes,
  historyMode = false,
  historyFeatures = [],
  open: openProp,
  onClose: onCloseProp,
}) {
  const dispatch = useDispatch();
  const { whatsNew, azAccountName } = useSelector((state) => state.account) || {};
  const {
    shouldShowModal = false,
    showCount = 0,
    headerSettings = {},
    features: stateFeatures = [],
  } = whatsNew || {};

  // Track which features are expanded (by feature id)
  const [expandedFeatures, setExpandedFeatures] = useState({});
  // Track which features have their "show more" content blocks expanded
  const [expandedContent, setExpandedContent] = useState({});
  // Dismiss checkbox state
  const [dismissChecked, setDismissChecked] = useState(false);

  const isOpen = historyMode ? (openProp || false) : shouldShowModal;
  const features = historyMode ? historyFeatures : stateFeatures;
  const mustScrollMode = !historyMode && showCount >= 3;

  const handleClose = useCallback(() => {
    if (historyMode) {
      if (onCloseProp) onCloseProp();
      return;
    }
    dispatch(dismissWhatsNew(dismissChecked));
    setDismissChecked(false);
  }, [dispatch, dismissChecked, historyMode, onCloseProp]);

  const toggleFeatureExpand = useCallback((featureId) => {
    setExpandedFeatures((prev) => ({
      ...prev,
      [featureId]: !prev[featureId],
    }));
  }, []);

  const toggleContentExpand = useCallback((featureId) => {
    setExpandedContent((prev) => ({
      ...prev,
      [featureId]: !prev[featureId],
    }));
  }, []);

  if (!isOpen || !features.length) {
    return null;
  }

  const renderContentBlock = (block) => {
    if (block.contentType === 'text') {
      return (
        <div
          key={block.id}
          className={classes.contentBlockText}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: block.contentValue }}
        />
      );
    }

    if (block.contentType === 'image') {
      const urls = parseImageUrls(
        block.contentValue,
        block.attachmentContainer,
        azAccountName,
      );
      return (
        <div key={block.id} className={classes.contentBlockImage}>
          {urls.map((url) => (
            <img
              key={url}
              src={url}
              alt=""
              className={classes.contentImage}
            />
          ))}
        </div>
      );
    }

    if (block.contentType === 'video') {
      let videoLink = block.contentValue || '';
      const vimeoMatch = videoLink.match(
        /vimeo\.com\/(\d+)(?:\/([a-f0-9]+))?/,
      );
      if (vimeoMatch) {
        const videoId = vimeoMatch[1];
        const hash = vimeoMatch[2];
        videoLink = hash
          ? `https://player.vimeo.com/video/${videoId}?h=${hash}`
          : `https://player.vimeo.com/video/${videoId}`;
      }

      return (
        <div key={block.id} className={classes.contentBlockVideo}>
          <div className={classes.videoContainer}>
            <VimeoVideo
              link={videoLink}
              title="Feature video"
              width="100%"
              height="100%"
              positionsY="center"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  const renderFeature = (feature) => {
    const {
      id,
      title,
      startDate,
      previewContentBlocks,
      contentBlocks = [],
    } = feature;

    // previewContentBlocks logic:
    // null = show all expanded, no "Show more"
    // 0 = show nothing, just the collapsed purple bar, no "Show more"
    // 1, 2, 3... = show that many blocks, "Show more" if more exist
    const hasCollapsibleContent = previewContentBlocks != null
      && contentBlocks.length > previewContentBlocks;
    const isContentExpanded = expandedContent[id] || false;

    const isMultiFeature = features.length > 1;
    const defaultExpanded = previewContentBlocks !== 0;
    const isFeatureExpanded = isMultiFeature
      ? (expandedFeatures[id] != null ? expandedFeatures[id] : defaultExpanded)
      : true;

    let visibleBlocks;
    if (previewContentBlocks === 0 && !isContentExpanded) {
      visibleBlocks = [];
    } else if (hasCollapsibleContent && !isContentExpanded) {
      visibleBlocks = contentBlocks.slice(0, previewContentBlocks);
    } else {
      visibleBlocks = contentBlocks;
    }

    const headerClassName = isMultiFeature
      ? `${classes.featureHeader} ${classes.featureHeaderCollapsible}`
      : classes.featureHeader;

    const dateLabel = formatDate(startDate);

    return (
      <div key={id} className={classes.featureCard}>
        {isMultiFeature ? (
          <button
            type="button"
            className={headerClassName}
            onClick={() => toggleFeatureExpand(id)}
          >
            <div className={classes.featureHeaderText}>
              <h3 className={classes.featureTitle}>{title}</h3>
              {dateLabel && (
                <div className={classes.featureDate}>{dateLabel}</div>
              )}
            </div>
            <ExpandMoreIcon
              className={
                `${classes.expandIcon} ${isFeatureExpanded ? classes.expandIconOpen : ''}`
              }
            />
          </button>
        ) : (
          <div className={headerClassName}>
            <div className={classes.featureHeaderText}>
              <h3 className={classes.featureTitle}>{title}</h3>
              {dateLabel && (
                <div className={classes.featureDate}>{dateLabel}</div>
              )}
            </div>
          </div>
        )}

        {isFeatureExpanded && (
          <div className={classes.featureContent}>
            {visibleBlocks.map(renderContentBlock)}

            {hasCollapsibleContent && (
              <button
                type="button"
                className={classes.showMoreButton}
                onClick={() => toggleContentExpand(id)}
              >
                {isContentExpanded ? 'Show less' : 'Show more'}
                <ExpandMoreIcon
                  className={
                    `${classes.showMoreIcon} ${isContentExpanded ? classes.showMoreIconOpen : ''}`
                  }
                />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // In must-scroll mode: hide X, disable backdrop close
  // In history mode or normal mode: X and backdrop close work
  const showCloseX = !mustScrollMode || historyMode;
  const allowBackdropClose = !mustScrollMode || historyMode;

  return (
    <Dialog
      open
      onClose={allowBackdropClose ? handleClose : undefined}
      aria-labelledby="whats-new-modal-title"
      classes={{ paper: classes.dialogPaper }}
      maxWidth="md"
      disableEscapeKeyDown={!allowBackdropClose}
    >
      <div
        className={classes.headerBar}
        style={
          headerSettings.headerBgColorStart || headerSettings.headerBgColorEnd
            ? {
              background: `linear-gradient(135deg, ${headerSettings.headerBgColorStart || '#5850EC'} 0%, ${headerSettings.headerBgColorEnd || '#7C3AED'} 100%)`,
            }
            : undefined
        }
      >
        <div className={classes.headerContent}>
          {showCloseX && (
            <IconButton
              className={classes.closeButton}
              onClick={handleClose}
              aria-label="close"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          )}
          <h2 id="whats-new-modal-title" className={classes.headline}>
            {headerSettings.headerTitle || "What's New in Connection Point"}
          </h2>
          {headerSettings.headerSubtitle && (
            <p className={classes.headerSubtitle}>
              {headerSettings.headerSubtitle}
            </p>
          )}
          {headerSettings.headerImageUrl && (
            <img
              src={headerSettings.headerImageUrl}
              alt=""
              className={classes.headerImage}
            />
          )}
        </div>
      </div>

      {mustScrollMode && !historyMode && (
        <div className={classes.scrollHint}>
          <ArrowDownwardIcon style={{ fontSize: 26, animation: 'bounceDown 1.4s ease-in-out infinite' }} />
          Scroll down to see all features
          <ArrowDownwardIcon style={{ fontSize: 26, animation: 'bounceDown 1.4s ease-in-out infinite' }} />
          <style>
            {`@keyframes bounceDown {
              0%, 100% { transform: translateY(0); }
              50% { transform: translateY(12px); }
            }`}
          </style>
        </div>
      )}

      <div
        className={classes.content}
      >
        {features.map(renderFeature)}

        {mustScrollMode && !historyMode && (
          <div className={classes.inlineFooter}>
            <label className={classes.dismissCheckboxRowInline} htmlFor="whats-new-dismiss-checkbox">
              <input
                id="whats-new-dismiss-checkbox"
                type="checkbox"
                checked={dismissChecked}
                onChange={(e) => setDismissChecked(e.target.checked)}
              />
              Got it &mdash; don&apos;t show again until new features are added
            </label>
            <button
              type="button"
              className={classes.closeFooterButtonPrimary}
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        )}
      </div>

      {!mustScrollMode && (
        <div className={classes.footer}>
          <button
            type="button"
            className={classes.closeFooterButton}
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      )}
    </Dialog>
  );
}

WhatsNewModal.propTypes = {
  classes: PropTypes.object.isRequired,
  historyMode: PropTypes.bool,
  historyFeatures: PropTypes.array,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default withStyles(WhatsNewModal, whatsNewModalStyle);
